import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@scio3/ui';

type Props = {
  open: boolean;
  onContinue: () => void;
};

/**
 * Auto-charge notice — scio1 parity for the `confirmService.confirm` popup shown
 * after a card update when the account has outstanding invoices
 * (paymentComponent.js afterSave, the `res.invoices.length` branch;
 * i18n key `subAccountConfirmAutoCharge`). scio3 previously documented this
 * branch but never rendered it, so a card update that triggered auto-charge
 * gave the user no confirmation popup at all.
 *
 * Informational only (single Close button, warning icon) — matching scio1's
 * `showOK:false, showCancel:true, cancel:'Close'`.
 */
export function AutoChargeNoticeDialog({ open, onContinue }: Props) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onContinue()}>
      <DialogContent
        className="sm:max-w-md flex flex-col overflow-hidden"
        data-testid="auto-charge-notice-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            {t('Payment Method Updated')}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2 text-sm text-foreground">
          {t('The card will be automatically charged for next invoices going forward')}
        </div>

        <DialogFooter>
          <Button onClick={onContinue} className="cursor-pointer">
            {t('Close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
